import { ProtectRole } from "@/components/ProtectRole";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useAnalyticsRanking,
  useAnalyticsRankingPerformance,
  useAnalyticsSummary,
  useGenerateAnalytics,
} from "@/hooks/use-analytics";
import { usePeriodeStore } from "@/hooks/use-periode";
import { apiClient } from "@/lib/api-client";
import { roundNumber } from "@/lib/utils";
import {
  Activity,
  ArrowRightLeft,
  Calendar,
  ChevronDown,
  Download,
  MoreHorizontal,
  RefreshCw,
} from "lucide-react";
import { motion } from "motion/react";
import React, { useEffect, useState } from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const formatRupiah = (value: number, decimals: number = 0) => {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

const formatRupiahCompact = (value: number) => {
  if (value >= 1000000000) {
    return `Rp ${(value / 1000000000).toFixed(2)} Miliar`;
  }
  if (value >= 1000000) {
    return `Rp ${(value / 1000000).toFixed(1)} Juta`;
  }
  if (value >= 1000) {
    return `Rp ${(value / 1000).toFixed(1)} Ribu`;
  }
  return `Rp ${value.toFixed(0)}`;
};

const getSingkatan = (name: string) => {
  const exclude = [
    "dan",
    "atau",
    "serta",
    "yang",
    "di",
    "ke",
    "dari",
    "pada",
    "dalam",
    "untuk",
    "dengan",
  ];
  let cleanName = name.replace(/\([^)]*\)/g, "").trim();

  if (cleanName.includes("/")) {
    const afterSlash = cleanName.split("/")[1].trim().split(" ")[0];
    if (afterSlash && afterSlash.length >= 2 && afterSlash.length <= 6) {
      return afterSlash.toUpperCase();
    }
  }

  return cleanName
    .split(/[\s-]+/)
    .filter(
      (word) => word.trim().length > 0 && !exclude.includes(word.toLowerCase()),
    )
    .map((word) => word.charAt(0).toUpperCase())
    .join("")
    .substring(0, 5);
};

const COLORS = ["#6366f1", "#a855f7", "#facc15", "#ec4899", "#3b82f6"];
const AVATAR_COLORS = [
  { bg: "#fee2e2", text: "#b91c1c" }, // red
  { bg: "#f3e8ff", text: "#7e22ce" }, // purple
  { bg: "#dcfce7", text: "#15803d" }, // green
  { bg: "#e0f2fe", text: "#0369a1" }, // blue
];

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background/95 backdrop-blur-md border rounded-lg shadow-xl p-3 text-sm">
        <p className="font-bold mb-1 max-w-50 truncate">
          {payload[0].payload.nama}
        </p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color }} className="font-medium">
            {entry.name || entry.dataKey}:{" "}
            {typeof entry.value === "number" && entry.value > 10000
              ? formatRupiahCompact(entry.value)
              : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const AnalyticsSection = () => {
  const [level, setLevel] = useState<string>("fakultas");
  const [viewMode, setViewMode] = useState<"analytics" | "table">("analytics");
  const [rankingLimit, setRankingLimit] = useState<number>(5);
  const [lastGenerated, setLastGenerated] = useState<number | null>(null);
  const [cooldown, setCooldown] = useState<number>(0);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isExportingCluster, setIsExportingCluster] = useState<boolean>(false);

  const { getYearAndSemester } = usePeriodeStore();
  const { year, semester, periodeId } = getYearAndSemester();

  const generateMutation = useGenerateAnalytics();

  useEffect(() => {
    const savedTime = localStorage.getItem("lastGeneratedAnalyticsTime");
    if (savedTime) {
      setLastGenerated(parseInt(savedTime, 10));
    }
  }, []);

  useEffect(() => {
    if (!lastGenerated) return;

    const checkCooldown = () => {
      const now = Date.now();
      const diff = now - lastGenerated;
      const fiveMinutes = 5 * 60 * 1000;

      if (diff < fiveMinutes) {
        setCooldown(Math.ceil((fiveMinutes - diff) / 1000));
      } else {
        setCooldown(0);
      }
    };

    checkCooldown();
    const interval = setInterval(checkCooldown, 1000);
    return () => clearInterval(interval);
  }, [lastGenerated]);

  const handleGenerate = () => {
    if (!periodeId) return;

    generateMutation.mutate(
      { year, semester, periodeID: periodeId },
      {
        onSuccess: () => {
          const now = Date.now();
          setLastGenerated(now);
          localStorage.setItem("lastGeneratedAnalyticsTime", now.toString());
        },
      },
    );
  };

  const handleExport = async () => {
    if (!periodeId) return;
    try {
      setIsExporting(true);
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      const queryParams = new URLSearchParams({
        year: year.toString(),
        semester: semester.toString(),
        periodeID: periodeId.toString(),
      });

      const response = await apiClient<Blob>(
        `${baseUrl}/api/v1/dosen-remun/analytics-70/export?${queryParams.toString()}`,
        {
          method: "GET",
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json",
          },
        },
      );

      const url = window.URL.createObjectURL(response);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Remunerasi 70% ${year}-${semester}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCluster = async () => {
    if (!periodeId) return;
    try {
      setIsExportingCluster(true);
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      const queryParams = new URLSearchParams({
        year: year.toString(),
        semester: semester.toString(),
        periodeID: periodeId.toString(),
      });

      const response = await apiClient<Blob>(
        `${baseUrl}/api/v1/dosen-remun/analytics-70/cluster/export?${queryParams.toString()}`,
        {
          method: "GET",
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json",
          },
        },
      );

      const url = window.URL.createObjectURL(response);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Cluster Remunerasi 70% ${year}-${semester}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export cluster failed:", error);
    } finally {
      setIsExportingCluster(false);
    }
  };

  const { data, isLoading, error } = useAnalyticsSummary({
    year,
    semester,
    periodeID: periodeId,
    level,
  });

  console.log({ data });

  const { data: rankingData, isLoading: rankingLoading } = useAnalyticsRanking({
    year,
    semester,
    periodeID: periodeId,
    limit: rankingLimit,
    sort_by: "rupiah",
  });

  const { data: rankingPerformanceData, isLoading: rankingPerformanceLoading } =
    useAnalyticsRankingPerformance({
      year,
      semester,
      periodeID: periodeId,
      limit: rankingLimit,
      sort_by: "point",
    });

  const chartData = React.useMemo(() => {
    if (!data?.entries) return [];
    return [...data.entries]
      .sort((a, b) => b.total_rupiah_70_bersih - a.total_rupiah_70_bersih)
      .map((entry) => ({
        ...entry,
        singkatan: getSingkatan(entry.nama),
      }));
  }, [data]);

  // const highestPointData = React.useMemo(() => {
  //   if (!data?.entries) return [];
  //   return [...data.entries].sort(
  //     (a, b) => b.rata_rata_total_point - a.rata_rata_total_point,
  //   );
  // }, [data]);

  if (!periodeId) {
    return (
      <Card className="border-dashed shadow-sm">
        <CardContent className="flex flex-col items-center justify-center h-48 text-muted-foreground">
          <Activity className="h-8 w-8 mb-4 opacity-50" />
          <p>Silakan pilih periode aktif terlebih dahulu</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-full rounded-xl" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive/50 bg-destructive/10">
        <CardContent className="flex flex-col items-center justify-center h-48 text-destructive">
          <p>Gagal memuat data analitik.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out font-sans">
      {/* Top Navigation / Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-muted/30 gap-4">
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <div className="flex p-1 bg-slate-100/80 rounded-xl overflow-x-auto w-full sm:w-auto shadow-inner border border-slate-200/50">
            <button
              onClick={() => setViewMode("analytics")}
              className={`relative flex items-center px-4 py-2 text-sm font-semibold whitespace-nowrap rounded-lg transition-colors z-10 ${viewMode === "analytics" ? "text-primary" : "text-slate-600 hover:text-slate-900"}`}
            >
              {viewMode === "analytics" && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute inset-0 bg-card rounded-lg shadow-sm border border-slate-200/50"
                  initial={false}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
              <span
                className={`relative z-20 flex items-center justify-center w-5 h-5 rounded-md text-[10px] mr-2 font-bold transition-colors ${viewMode === "analytics" ? "bg-primary/10 text-primary" : "bg-slate-200/50 text-slate-500"}`}
              >
                01
              </span>
              <span className="relative z-20">Overview</span>
            </button>

            <button
              onClick={() => setViewMode("table")}
              className={`relative flex items-center px-4 py-2 text-sm font-semibold whitespace-nowrap rounded-lg transition-colors z-10 ${viewMode === "table" ? "text-primary" : "text-slate-600 hover:text-slate-900"}`}
            >
              {viewMode === "table" && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute inset-0 bg-card rounded-lg shadow-sm border border-slate-200/50"
                  initial={false}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
              <span
                className={`relative z-20 flex items-center justify-center w-5 h-5 rounded-md text-[10px] mr-2 font-bold transition-colors ${viewMode === "table" ? "bg-primary/10 text-primary" : "bg-slate-200/50 text-slate-500"}`}
              >
                02
              </span>
              <span className="relative z-20">Table Data</span>
            </button>

            <div className="w-px bg-slate-300/50 mx-1 my-2"></div>

            <button
              onClick={() => setLevel(level === "prodi" ? "fakultas" : "prodi")}
              className="relative flex items-center px-4 py-2 text-sm font-semibold whitespace-nowrap rounded-lg text-slate-600 hover:text-slate-900 hover:bg-white/60 transition-colors group"
            >
              <span className="flex items-center justify-center w-5 h-5 rounded-md bg-slate-200/50 text-[10px] mr-2 font-bold text-slate-500">
                03
              </span>
              <span>Switch to {level === "prodi" ? "Fakultas" : "Prodi"}</span>
              <ArrowRightLeft className="w-3.5 h-3.5 ml-2 text-slate-400 group-hover:text-primary transition-colors" />
            </button>
          </div>

          <div className="hidden sm:flex items-center border-l border-muted/30 pl-3">
            <span className="text-[11px] px-2 py-1 bg-primary/10 text-primary font-bold rounded-md uppercase tracking-wider whitespace-nowrap">
              Menampilkan: {level === "prodi" ? "Prodi" : "Fakultas"}
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-2 self-start sm:self-auto">
          <div className="flex items-center px-3 py-1.5 bg-background border rounded-md text-sm font-medium shadow-sm">
            <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
            <span>
              Periode {year}-{semester}
            </span>
          </div>
          <ProtectRole allowedRoles={[]}>
            <button
              onClick={handleGenerate}
              disabled={cooldown > 0 || generateMutation.isPending}
              className={`flex items-center px-3 py-1.5 border rounded-md text-sm font-medium shadow-sm transition-colors ${
                cooldown > 0 || generateMutation.isPending
                  ? "bg-muted text-muted-foreground cursor-not-allowed"
                  : "bg-primary text-primary-foreground hover:bg-primary/90"
              }`}
            >
              <RefreshCw
                className={`w-4 h-4 mr-2 ${generateMutation.isPending ? "animate-spin" : ""}`}
              />
              {generateMutation.isPending
                ? "Generating..."
                : cooldown > 0
                  ? `Cooldown (${Math.ceil(cooldown / 1000)}s)`
                  : "Generate Analytic"}
            </button>
          </ProtectRole>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                disabled={isExporting || isExportingCluster}
                className={`flex items-center px-3 py-1.5 border rounded-md text-sm font-medium shadow-sm transition-colors ${
                  isExporting || isExportingCluster
                    ? "bg-muted text-muted-foreground cursor-not-allowed"
                    : "bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-600"
                }`}
              >
                <Download
                  className={`w-4 h-4 mr-2 ${isExporting || isExportingCluster ? "animate-bounce" : ""}`}
                />
                {isExporting || isExportingCluster ? "Exporting..." : "Export"}
                <ChevronDown className="w-4 h-4 ml-1 opacity-70" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={handleExport} disabled={isExporting}>
                Export Remun 70%
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleExportCluster}
                disabled={isExportingCluster}
              >
                Export Cluster
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {viewMode === "table" ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
        >
          {/* Custom Header */}
          <div className="sticky top-4 z-20 flex items-center px-6 py-3 text-[11px] font-bold text-muted-foreground uppercase tracking-wider bg-card/95 backdrop-blur-md shadow-[0_4px_20px_rgba(0,0,0,0.05)] rounded-xl border border-slate-200/60">
            <div className="w-12 text-center">No</div>
            <div className="w-24">Kode</div>
            <div className="flex-1">
              Unit ({level === "prodi" ? "Prodi" : "Fakultas"})
            </div>
            <div className="w-32 text-right">Dosen</div>
            <div className="w-48 text-right">Total 70% Bersih</div>
            <div className="w-48 text-right">Total 30% Realisasi</div>
          </div>

          <div className="space-y-3 pb-8">
            {chartData.map((item, index) => (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.02 }}
                key={item.kode}
                className="flex items-center px-6 py-4 bg-card border border-slate-100 rounded-xl shadow-sm hover:shadow-md hover:border-primary/20 transition-all group"
              >
                <div className="w-12 flex justify-center">
                  <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-xs font-bold group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    {index + 1}
                  </div>
                </div>
                <div className="w-24">
                  <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-semibold rounded-md group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                    {item.kode}
                  </span>
                </div>
                <div className="flex-1 flex flex-col pr-4">
                  <span className="font-bold text-slate-800 text-[13px] leading-tight group-hover:text-primary transition-colors">
                    {item.nama}
                  </span>
                  <span className="text-[11px] text-muted-foreground mt-1 font-medium">
                    {item.singkatan}
                  </span>
                </div>
                <div className="w-32 text-right">
                  <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs font-bold rounded-md border border-blue-100">
                    {item.total_dosen}{" "}
                    <span className="font-normal ml-1">Org</span>
                  </span>
                </div>
                <div className="w-48 text-right flex flex-col items-end">
                  <span className="font-black text-emerald-600 text-[16px]">
                    {formatRupiah(item.total_rupiah_70_bersih)}
                  </span>
                </div>
                <div className="w-48 text-right flex flex-col items-end">
                  <span className="font-black text-indigo-600 text-[16px]">
                    {formatRupiah(item.total_rupiah_30_realisasi || 0)}
                  </span>
                </div>
              </motion.div>
            ))}

            {/* Grand Total Row */}
            {data?.grand_total && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: chartData.length * 0.02 }}
                className="sticky bottom-4 z-20 flex items-center px-6 py-4 bg-card border-2 border-primary/20 rounded-xl shadow-[0_-4px_20px_rgba(0,0,0,0.08)] mt-6"
              >
                <div className="w-12"></div>
                <div className="w-24"></div>
                <div className="flex-1">
                  <span className="font-bold text-primary text-sm uppercase tracking-wider">
                    Grand Total
                  </span>
                </div>
                <div className="w-32 text-right">
                  <span className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary text-xs font-bold rounded-md">
                    {data.grand_total.total_dosen}{" "}
                    <span className="font-normal ml-1">Org</span>
                  </span>
                </div>
                <div className="w-48 text-right flex flex-col items-end">
                  <span className="font-black text-primary text-[14px]">
                    {formatRupiah(
                      roundNumber(data.grand_total.total_rupiah_70_bersih, 2),
                      2,
                    )}
                  </span>
                </div>
                <div className="w-48 text-right flex flex-col items-end">
                  <span className="font-black text-primary text-[14px]">
                    {formatRupiah(
                      roundNumber(
                        data.grand_total.total_rupiah_30_realisasi || 0,
                        2,
                      ),
                      2,
                    )}
                  </span>
                </div>
              </motion.div>
            )}
          </div>

          {/* Right Table: Ranking Dosen */}
          <div className="space-y-4 w-full overflow-hidden">
            <div className="flex items-center justify-between px-1">
              <h3 className="text-lg font-bold text-slate-800">
                Ranking Dosen
              </h3>
              <Select
                value={rankingLimit.toString()}
                onValueChange={(value) => setRankingLimit(Number(value))}
              >
                <SelectTrigger className="h-8 w-27.5 text-xs bg-background">
                  <SelectValue placeholder="Limit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">Top 5</SelectItem>
                  <SelectItem value="10">Top 10</SelectItem>
                  <SelectItem value="20">Top 20</SelectItem>
                  <SelectItem value="50">Top 50</SelectItem>
                  <SelectItem value="100">Top 100</SelectItem>
                  <SelectItem value="1000">Top 1000</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="overflow-x-auto pb-4">
              <div className="min-w-150 space-y-3 pb-8">
                <div className="sticky top-4 z-20 flex items-center px-6 py-3 text-[11px] font-bold text-muted-foreground uppercase tracking-wider bg-card/95 backdrop-blur-md shadow-[0_4px_20px_rgba(0,0,0,0.05)] rounded-xl border border-slate-200/60">
                  <div className="w-12 text-center">Rank</div>
                  <div className="flex-1">Nama & NIP</div>
                  <div className="w-48 hidden md:block">Unit Kerja</div>
                  <div className="w-32 text-right">Points</div>
                  <div className="w-48 text-right">Total 70% Bersih</div>
                </div>

                <div className="space-y-3">
                  {rankingLoading ? (
                    <div className="px-6 py-8 text-center text-muted-foreground">
                      Loading ranking...
                    </div>
                  ) : rankingData?.data?.length ? (
                    rankingData.data.map((item, index) => (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index > 20 ? 0 : index * 0.02 }}
                        key={item.niu || index}
                        className="flex items-center px-6 py-4 bg-card border border-slate-100 rounded-xl shadow-sm hover:shadow-md hover:border-primary/20 transition-all group"
                      >
                        <div className="w-12 flex justify-center">
                          <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-xs font-bold group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                            {index + 1}
                          </div>
                        </div>
                        <div className="flex-1 flex flex-col pr-4">
                          <span className="font-bold text-slate-800 text-[13px] leading-tight group-hover:text-primary transition-colors">
                            {item.nama}
                          </span>
                          <span className="text-[11px] text-muted-foreground mt-1 font-medium">
                            NIP: {item.nip || item.niu || "-"}
                          </span>
                        </div>
                        <div className="w-48 hidden md:flex flex-col pr-4">
                          <span
                            className="text-[11px] font-medium text-slate-700 truncate"
                            title={item.prodi}
                          >
                            {item.prodi || "-"}
                          </span>
                          <span
                            className="text-[10px] text-muted-foreground truncate"
                            title={item.fakultas}
                          >
                            {item.fakultas || "-"}
                          </span>
                        </div>
                        <div className="w-32 text-right">
                          <span className="inline-flex items-center px-2 py-1 bg-amber-50 text-amber-700 text-xs font-bold rounded-md border border-amber-100">
                            {item.total_point
                              ? Number(item.total_point).toLocaleString()
                              : 0}
                          </span>
                        </div>
                        <div className="w-48 text-right flex flex-col items-end">
                          <span className="font-black text-emerald-600 text-[16px]">
                            {formatRupiah(
                              roundNumber(item.total_rupiah_70_bersih, 2),
                              2,
                            )}
                          </span>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="px-6 py-8 text-center text-muted-foreground">
                      Tidak ada data ranking.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Top Row: Summary, Gauge, List */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Summary Card */}
            <Card className="rounded-xl shadow-sm border-muted/40">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-lg font-bold">Summary</CardTitle>
                <MoreHorizontal className="w-5 h-5 text-muted-foreground" />
              </CardHeader>
              <CardContent className="space-y-3 pt-2">
                <div className="flex justify-between items-center px-4 py-3 rounded-lg bg-blue-50/70 text-blue-700 border border-blue-100/50">
                  <span className="flex items-center text-sm font-medium">
                    <span className="w-2 h-2 rounded-full border border-blue-500 mr-2"></span>{" "}
                    Total Dosen
                  </span>
                  <span className="font-bold">
                    {data?.grand_total?.total_dosen?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center px-4 py-3 rounded-lg bg-emerald-50/70 text-emerald-700 border border-emerald-100/50">
                  <span className="flex items-center text-sm font-medium">
                    <span className="w-2 h-2 rounded-full border border-emerald-500 mr-2"></span>{" "}
                    Dapat Remunerasi
                  </span>
                  <span className="font-bold">
                    {data?.grand_total?.total_dosen_dapat?.toLocaleString() ||
                      0}
                  </span>
                </div>
                <div className="flex justify-between items-center px-4 py-3 rounded-lg bg-rose-50/70 text-rose-700 border border-rose-100/50">
                  <span className="flex items-center text-sm font-medium">
                    <span className="w-2 h-2 rounded-full border border-rose-500 mr-2"></span>{" "}
                    Eligible (Tidak Dapat)
                  </span>
                  <span className="font-bold">
                    {data?.grand_total?.eligible_dapat_remun?.total_dosen_tidak_dapat?.toLocaleString() ||
                      0}
                  </span>
                </div>
                <div className="flex justify-between items-center px-4 py-3 rounded-lg bg-amber-50/70 text-amber-700 border border-amber-100/50">
                  <span className="flex items-center text-sm font-medium">
                    <span className="w-2 h-2 rounded-full border border-amber-500 mr-2"></span>{" "}
                    Tidak Eligible
                  </span>
                  <span className="font-bold">
                    {data?.grand_total?.total_dosen_tidak_eligible?.toLocaleString() ||
                      0}
                  </span>
                </div>
                <div className="flex justify-between items-center px-4 py-3 rounded-lg bg-purple-50/70 text-purple-700 border border-purple-100/50">
                  <span className="flex items-center text-sm font-medium">
                    <span className="w-2 h-2 rounded-full border border-purple-500 mr-2"></span>{" "}
                    Total {level === "prodi" ? "Prodi" : "Fakultas"}
                  </span>
                  <span className="font-bold">
                    {data?.entries?.length || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center px-4 py-3 rounded-lg bg-slate-50/70 text-slate-700 border border-slate-200/50">
                  <span className="flex items-center text-sm font-medium">
                    <span className="w-2 h-2 rounded-full border border-slate-500 mr-2"></span>{" "}
                    Total Remun 70%
                  </span>
                  <span className="font-bold">
                    {formatRupiahCompact(
                      data?.grand_total?.total_rupiah_70_bersih || 0,
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center px-4 py-3 rounded-lg bg-indigo-50/70 text-indigo-700 border border-indigo-100/50">
                  <span className="flex items-center text-sm font-medium">
                    <span className="w-2 h-2 rounded-full border border-indigo-500 mr-2"></span>{" "}
                    Total 30% Realisasi
                  </span>
                  <span className="font-bold">
                    {formatRupiahCompact(
                      data?.grand_total?.total_rupiah_30_realisasi || 0,
                    )}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Gauge Chart Card */}
            <Card className="rounded-xl shadow-sm border-muted/40">
              <CardHeader className="flex flex-row items-center justify-between pb-0">
                <CardTitle className="text-lg font-bold">
                  Top 5 by Remunerasi
                </CardTitle>
                <MoreHorizontal className="w-5 h-5 text-muted-foreground" />
              </CardHeader>
              <CardContent className="flex flex-col items-center justify-center pt-8 pb-4">
                <div className="h-55 w-full relative mt-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                      <Pie
                        data={chartData.slice(0, 5)}
                        cx="50%"
                        cy="90%"
                        startAngle={180}
                        endAngle={0}
                        innerRadius={110}
                        outerRadius={150}
                        paddingAngle={2}
                        dataKey="total_rupiah_70_bersih"
                        stroke="none"
                      >
                        {chartData.slice(0, 5).map((_entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-x-0 bottom-6 flex flex-col items-center justify-center text-center">
                    <span className="text-xs text-muted-foreground font-medium mb-1">
                      Estimasi Total Remunerasi
                    </span>
                    <span className="text-3xl font-black tracking-tight">
                      {formatRupiahCompact(
                        data?.grand_total.total_rupiah_70_bersih || 0,
                      ).replace(" Miliar", " M")}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Highest Performance List */}
            <Card className="rounded-xl shadow-sm border-muted/40">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <CardTitle className="text-lg font-bold">
                  Highest Performance
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Select
                    value={rankingLimit.toString()}
                    onValueChange={(value) => setRankingLimit(Number(value))}
                  >
                    <SelectTrigger className="h-7 w-25 text-xs bg-background">
                      <SelectValue placeholder="Limit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">Top 5</SelectItem>
                      <SelectItem value="10">Top 10</SelectItem>
                      <SelectItem value="20">Top 20</SelectItem>
                      <SelectItem value="50">Top 50</SelectItem>
                      <SelectItem value="100">Top 100</SelectItem>
                      <SelectItem value="1000">Top 1000</SelectItem>
                    </SelectContent>
                  </Select>
                  <MoreHorizontal className="w-5 h-5 text-muted-foreground hidden sm:block" />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <Tabs defaultValue="without-position" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-4">
                    <TabsTrigger value="without-position" className="text-xs">
                      Tanpa Struktural
                    </TabsTrigger>
                    <TabsTrigger value="with-position" className="text-xs">
                      Dengan Struktural
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent
                    value="without-position"
                    className="m-0 space-y-0 mt-2"
                  >
                    <div className="flex text-[11px] text-muted-foreground font-semibold mb-3 px-1 uppercase tracking-wider">
                      <div className="flex-1">Dosen</div>
                      <div className="w-[35%] text-right">
                        Point Performance
                      </div>
                    </div>
                    <div className="space-y-4 overflow-y-auto max-h-87.5 pr-2">
                      {rankingPerformanceLoading ? (
                        <div className="space-y-3">
                          <Skeleton className="h-8 w-full" />
                          <Skeleton className="h-8 w-full" />
                          <Skeleton className="h-8 w-full" />
                          <Skeleton className="h-8 w-full" />
                          <Skeleton className="h-8 w-full" />
                        </div>
                      ) : (
                        rankingPerformanceData?.data.map((item, i) => {
                          const color = AVATAR_COLORS[i % AVATAR_COLORS.length];
                          const rawName = item.nama
                            .replace(
                              /Prof\.|Dr\.|Drs\.|Ir\.|M\.Pd\.|S\.Pd\./g,
                              "",
                            )
                            .trim();
                          const initial = rawName.charAt(0).toUpperCase();
                          return (
                            <div
                              key={item.niu}
                              className="flex items-center text-sm px-1"
                            >
                              <div className="flex-1 flex items-center overflow-hidden pr-2">
                                <div
                                  className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-xs font-bold mr-3"
                                  style={{
                                    backgroundColor: color.bg,
                                    color: color.text,
                                  }}
                                >
                                  {initial}
                                </div>
                                <div className="flex flex-col truncate">
                                  <span
                                    className="font-medium text-[13px] truncate"
                                    title={item.nama}
                                  >
                                    {item.nama}
                                  </span>
                                  <span
                                    className="text-[10px] text-muted-foreground truncate"
                                    title={item.prodi || item.fakultas}
                                  >
                                    {item.prodi || item.fakultas}
                                  </span>
                                </div>
                              </div>
                              <div className="w-[35%] text-right font-bold text-amber-600 text-[12px]">
                                {Number(
                                  item.point_performance,
                                ).toLocaleString()}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent
                    value="with-position"
                    className="m-0 space-y-0 mt-2"
                  >
                    <div className="flex text-[11px] text-muted-foreground font-semibold mb-3 px-1 uppercase tracking-wider">
                      <div className="flex-1">Dosen</div>
                      <div className="w-[35%] text-right">Total 70% Bersih</div>
                    </div>
                    <div className="space-y-4 overflow-y-auto max-h-87.5 pr-2">
                      {rankingLoading ? (
                        <div className="space-y-3">
                          <Skeleton className="h-8 w-full" />
                          <Skeleton className="h-8 w-full" />
                          <Skeleton className="h-8 w-full" />
                          <Skeleton className="h-8 w-full" />
                          <Skeleton className="h-8 w-full" />
                        </div>
                      ) : (
                        rankingData?.data.map((item, i) => {
                          const color = AVATAR_COLORS[i % AVATAR_COLORS.length];
                          const rawName = item.nama
                            .replace(
                              /Prof\.|Dr\.|Drs\.|Ir\.|M\.Pd\.|S\.Pd\./g,
                              "",
                            )
                            .trim();
                          const initial = rawName.charAt(0).toUpperCase();
                          return (
                            <div
                              key={item.niu}
                              className="flex items-center text-sm px-1"
                            >
                              <div className="flex-1 flex items-center overflow-hidden pr-2">
                                <div
                                  className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-xs font-bold mr-3"
                                  style={{
                                    backgroundColor: color.bg,
                                    color: color.text,
                                  }}
                                >
                                  {initial}
                                </div>
                                <div className="flex flex-col truncate">
                                  <span
                                    className="font-medium text-[13px] truncate"
                                    title={item.nama}
                                  >
                                    {item.nama}
                                  </span>
                                  <span
                                    className="text-[10px] text-muted-foreground truncate"
                                    title={item.prodi}
                                  >
                                    {item.prodi}
                                  </span>
                                </div>
                              </div>
                              <div className="w-[35%] text-right font-bold text-slate-700 text-[12px]">
                                {formatRupiahCompact(
                                  item.total_rupiah_70_bersih,
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Middle Row: Small Stats */}
          {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="rounded-xl shadow-md border-transparent bg-slate-900 text-white">
              <CardContent className="p-4 flex flex-col justify-between h-25">
                <div className="flex justify-between items-start">
                  <span className="text-xs font-medium text-slate-400">
                    Total Remun 70%
                  </span>
                  <MoreHorizontal className="w-4 h-4 text-slate-400 opacity-50" />
                </div>
                <div className="flex items-end justify-between">
                  <span className="text-2xl font-bold">
                    {formatRupiahCompact(
                      data?.grand_total.total_rupiah_70_bersih || 0,
                    )}
                  </span>
                  <span className="text-[10px] font-semibold bg-white/20 text-white px-1.5 py-0.5 rounded-sm">
                    +100% ↑
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-xl shadow-md border-transparent bg-indigo-900 text-white">
              <CardContent className="p-4 flex flex-col justify-between h-25">
                <div className="flex justify-between items-start">
                  <span className="text-xs font-medium text-indigo-300">
                    Total 30% Realisasi
                  </span>
                  <MoreHorizontal className="w-4 h-4 text-indigo-300 opacity-50" />
                </div>
                <div className="flex items-end justify-between">
                  <span className="text-2xl font-bold">
                    {formatRupiahCompact(
                      data?.grand_total.total_rupiah_30_realisasi || 0,
                    )}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-xl shadow-sm border-muted/40">
              <CardContent className="p-4 flex flex-col justify-between h-25">
                <div className="flex justify-between items-start">
                  <span className="text-xs font-medium text-muted-foreground">
                    Avg Points
                  </span>
                  <MoreHorizontal className="w-4 h-4 text-muted-foreground opacity-50" />
                </div>
                <div className="flex items-end justify-between">
                  <span className="text-2xl font-bold">
                    {avgPoints.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-xl shadow-sm border-muted/40">
              <CardContent className="p-4 flex flex-col justify-between h-25">
                <div className="flex justify-between items-start">
                  <span className="text-xs font-medium text-muted-foreground">
                    Maks Points
                  </span>
                  <MoreHorizontal className="w-4 h-4 text-muted-foreground opacity-50" />
                </div>
                <div className="flex items-end justify-between">
                  <span className="text-2xl font-bold">
                    {highestPointData[0]?.rata_rata_total_point.toLocaleString() ||
                      0}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div> */}

          {/* Bottom Row: Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Trend Chart */}
            <Card className="rounded-xl shadow-sm border-muted/40">
              <CardHeader className="flex flex-row items-center justify-between pb-6">
                <CardTitle className="text-lg font-bold">
                  Remunerasi & Points Trend
                </CardTitle>
                <div className="flex items-center space-x-3 text-xs">
                  <span className="flex items-center text-[#a855f7] font-medium">
                    <span className="w-2 h-2 rounded-full bg-[#a855f7] mr-1.5"></span>{" "}
                    Remunerasi
                  </span>
                  <span className="flex items-center text-[#22c55e] font-medium">
                    <span className="w-2 h-2 rounded-full bg-[#22c55e] mr-1.5"></span>{" "}
                    Score
                  </span>
                  <MoreHorizontal className="w-4 h-4 text-muted-foreground ml-2" />
                </div>
              </CardHeader>
              <CardContent className="h-62.5 pt-0">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData.slice(0, 10)}
                    margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      opacity={0.3}
                    />
                    <XAxis
                      dataKey="singkatan"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 10, fill: "#888" }}
                      dy={10}
                    />
                    <YAxis
                      yAxisId="left"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 10, fill: "#888" }}
                      tickFormatter={(v) => formatRupiahCompact(v)}
                    />
                    <YAxis yAxisId="right" orientation="right" hide />
                    <Tooltip
                      content={<CustomTooltip />}
                      cursor={{ stroke: "#e2e8f0", strokeWidth: 2 }}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="total_rupiah_70_bersih"
                      stroke="#a855f7"
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{
                        r: 5,
                        fill: "#a855f7",
                        stroke: "#fff",
                        strokeWidth: 2,
                      }}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="rata_rata_total_point"
                      stroke="#22c55e"
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{
                        r: 5,
                        fill: "#22c55e",
                        stroke: "#fff",
                        strokeWidth: 2,
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Metrics Chart */}
            <Card className="rounded-xl shadow-sm border-muted/40">
              <CardHeader className="flex flex-row items-center justify-between pb-6">
                <CardTitle className="text-lg font-bold">
                  Unit vs Dosen Metrics
                </CardTitle>
                <div className="flex items-center space-x-3 text-xs">
                  <span className="flex items-center text-[#6366f1] font-medium">
                    <span className="w-2 h-2 rounded-full bg-[#6366f1] mr-1.5"></span>{" "}
                    Dosen
                  </span>
                  <span className="flex items-center text-slate-800 font-medium">
                    <span className="w-2 h-2 rounded-full bg-slate-800 mr-1.5"></span>{" "}
                    Ratio
                  </span>
                  <MoreHorizontal className="w-4 h-4 text-muted-foreground ml-2" />
                </div>
              </CardHeader>
              <CardContent className="h-62.5 pt-0">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={chartData.slice(0, 10)}
                    margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      opacity={0.3}
                    />
                    <XAxis
                      dataKey="singkatan"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 10, fill: "#888" }}
                      dy={10}
                    />
                    <YAxis
                      yAxisId="left"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 10, fill: "#888" }}
                    />
                    <YAxis yAxisId="right" orientation="right" hide />
                    <Tooltip
                      content={<CustomTooltip />}
                      cursor={{ fill: "transparent" }}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="total_dosen"
                      fill="#6366f1"
                      radius={[6, 6, 0, 0]}
                      barSize={40}
                    />
                    <Line
                      yAxisId="right"
                      type="linear"
                      dataKey="rata_rata_total_point"
                      stroke="#1e293b"
                      strokeWidth={2}
                      dot={{
                        r: 4,
                        fill: "#fff",
                        stroke: "#1e293b",
                        strokeWidth: 2,
                      }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      )}
    </div>
  );
};
